import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import * as analytics from 'analytics/Analytics';
import {SequencePrime} from 'biojs/BioConstants';
import {formatPolymerResidueSubcount} from 'biojs/Util';
import {ActionOrigin, MOLBIO_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {convertStrandToDisplayName, Side} from 'constants/MolbioConstants';
import {
  BA_ORIENTATION_TO_STRAND,
  BasicPolymerInfo,
  Endpoints,
  FragmentProductionMethod,
  OPPOSITE_BA_ORIENTATION,
} from 'molbio/bulk-assembly/BulkAssemblyConstants';
import {
  BA_EMPTY_VALUE,
  baNumber,
  BANumberValue,
  BARawTextValue,
  BulkAssemblyOrientation,
  isBANumber,
} from 'molbio/bulk-assembly/BulkAssemblyValues';
import {FragmentDataSetters} from 'molbio/bulk-assembly/polymer-map-modals/hooks/useFragmentDataSetters';
import EndpointRestrictionEnzymeInput from 'molbio/bulk-assembly/polymer-map-modals/inputs/EndpointRestrictionEnzymeInput';
import PrimerSearchInput from 'molbio/bulk-assembly/polymer-map-modals/inputs/PrimerSearchInput';
import SelectionEndpointInput, {
  SelectionEndpointInputProps,
} from 'molbio/bulk-assembly/polymer-map-modals/inputs/SelectionEndpointInput';
import {PolymerTitle} from 'molbio/bulk-assembly/polymer-map-modals/PolymerTitle';
import {RestrictionEnzymeInfo} from 'molbio/bulk-assembly/util/EnzymeUtil';
import {
  getDisplayToModelConverters,
  getModelToDisplayConverters,
  getSnappableCutsiteEndpoints,
  getValueForOrientation,
  handleEndpointChange,
  getBasicFragmentSelectionDetails,
  getEndpointAwareCutsiteIndices,
} from 'molbio/bulk-assembly/util/FragmentEndpointUtil';
import FragmentVisualization from 'molbio/components/visualizations/FragmentVisualization';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_DARKER, RED_DARK} from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';

/**
 * Information used for displaying a fragment in the fragment modal and allowing configuration.
 */
export interface FragmentModalFragment {
  cid: string | null;
  binCid: string | null;
  endpoints: Endpoints;
  orientation: BulkAssemblyOrientation;
  versionDbId: number;
  polymerId: string;
  hadStaleVersion: boolean;
  color: string;
  binName: string | null;
  showPrimerSearch: boolean;
  preferredPrimer5Id: string | null;
  preferredPrimer3Id: string | null;
  restrictionEnzymeId5: number | null;
  restrictionEnzymeId3: number | null;
  basicPolymerInfo: BasicPolymerInfo;
  fragmentProductionMethod: FragmentProductionMethod;
  canUpdateRestrictionEnzymes: boolean;
  constructFragmentIndex: number | null;
}

export type FragmentCardProps = FragmentDataSetters &
  Omit<
    FragmentModalFragment,
    | 'cid'
    | 'binCid'
    | 'polymer'
    | 'versionDbId'
    | 'restrictionEnzymeId5'
    | 'restrictionEnzymeId3'
    | 'fragmentProductionMethod'
    | 'constructFragmentIndex'
    | 'basicPolymerInfo'
  > & {
    assemblyId: string;
    canSpecifyEndpoints: boolean;
    isReadOnly: boolean;
    validationFailureMessage: string | undefined;
    restrictionEnzymeInfo5: RestrictionEnzymeInfo;
    restrictionEnzymeInfo3: RestrictionEnzymeInfo;
    basicPolymerInfo?: BasicPolymerInfo;
  };

function getSelectionDisplayProps(
  endpoints: Endpoints,
  setEndpoints: (endpoints: Endpoints) => void,
  orientation: BulkAssemblyOrientation,
  setOrientation: (orientation: BulkAssemblyOrientation) => void,
  startIndex: number,
  circular: boolean,
  restrictionEnzymeInfo5: RestrictionEnzymeInfo,
  restrictionEnzymeInfo3: RestrictionEnzymeInfo,
  singleStranded: boolean,
  sequenceLength: number
): {
  start: SelectionEndpointInputProps;
  end: SelectionEndpointInputProps;
} {
  const displayToModelConverters = getDisplayToModelConverters(startIndex);
  const modelToDisplayConverters = getModelToDisplayConverters(startIndex);

  function getProps(side: Side): Omit<SelectionEndpointInputProps, 'side' | 'displayCutsites'> {
    const currValue = endpoints[side];
    return {
      displayValue: isBANumber(currValue) ? modelToDisplayConverters[side](currValue.value) : null,
      setFromDisplay: (displayValue: number | null): void => {
        if (displayValue == null) {
          setEndpoints({...endpoints, [side]: BA_EMPTY_VALUE});
          return;
        }
        const modelValue = displayToModelConverters[side](displayValue);
        const {orientation: newOrientation, ...newEndpoints} = handleEndpointChange(
          {...endpoints, [side]: baNumber(modelValue)},
          orientation,
          circular,
          singleStranded
        );
        setEndpoints(newEndpoints);
        if (newOrientation != null) {
          setOrientation(newOrientation);
        }
      },
    };
  }
  const startRestrictionEnzymeInfo = restrictionEnzymeInfo5;
  const endRestrictionEnzymeInfo = restrictionEnzymeInfo3;

  return {
    start: {
      side: 'start',
      displayCutsites:
        startRestrictionEnzymeInfo.cutsiteIndices != null
          ? getEndpointAwareCutsiteIndices(
              startRestrictionEnzymeInfo.cutsiteIndices,
              sequenceLength,
              'start'
            ).map((cutsiteIndex) =>
              modelToDisplayConverters[getValueForOrientation(orientation, 'start', 'end')](cutsiteIndex)
            )
          : null,
      ...getProps(getValueForOrientation(orientation, 'start', 'end')),
    },
    end: {
      side: 'end',
      displayCutsites:
        endRestrictionEnzymeInfo.cutsiteIndices != null
          ? getEndpointAwareCutsiteIndices(
              endRestrictionEnzymeInfo.cutsiteIndices,
              sequenceLength,
              'end'
            ).map((cutsiteIndex) =>
              modelToDisplayConverters[getValueForOrientation(orientation, 'end', 'start')](cutsiteIndex)
            )
          : null,
      ...getProps(getValueForOrientation(orientation, 'end', 'start')),
    },
  };
}

function getEnzymeDisplayInfo(
  restrictionEnzymeInfo: RestrictionEnzymeInfo,
  position: BANumberValue | BARawTextValue
): number | null {
  if (
    restrictionEnzymeInfo.cutsiteIndices != null &&
    isBANumber(position) &&
    restrictionEnzymeInfo.cutsiteIndices.includes(position.value)
  ) {
    return restrictionEnzymeInfo.restrictionEnzymeId;
  }
  return null;
}

function CardHeader({polymerId, binName}: {polymerId: string; binName: string | null}): JSX.Element {
  return (
    <div>
      {binName && (
        <div
          className={css(styles.binName)}
          {...getTestProps({component: 'FragmentCard', element: 'div', key: 'binName'})}
          data-amp-safe={false}
        >
          {binName} /
        </div>
      )}
      <PolymerTitle polymerId={polymerId} />
    </div>
  );
}

/**
 * A card representing a single bulk assembly fragment that provides a UI to adjust the endpoints
 * and orientation of the input used. Transparently handles updating when the Backbone Selection model
 * endpoints change and mirrors selection endpoints from the inputs back to the Backbone model.
 *
 * Includes an SVG visualization of the selection and a button to exchange the start and end selection endpoints.
 *
 * When the card is inactive, a condensed view is shown which hides the inputs and visualization.
 *
 * This component is controlled, so it takes in callbacks to update the selection state and strand state.
 */
export default function FragmentCardContents({
  assemblyId,
  canSpecifyEndpoints,
  preferredPrimer5Id,
  setPreferredPrimer5Id,
  preferredPrimer3Id,
  setPreferredPrimer3Id,
  restrictionEnzymeInfo5,
  setRestrictionEnzymeId5,
  restrictionEnzymeInfo3,
  setRestrictionEnzymeId3,
  endpoints,
  setEndpoints,
  orientation,
  setOrientation,
  hadStaleVersion,
  color,
  binName,
  showPrimerSearch,
  canUpdateRestrictionEnzymes,
  isReadOnly,
  polymerId,
  basicPolymerInfo,
  validationFailureMessage,
}: FragmentCardProps): JSX.Element {
  if (basicPolymerInfo == null) {
    return (
      <>
        <CardHeader polymerId={polymerId} binName={binName} />
        <CenteredHorizontalThrobber />
      </>
    );
  }

  const {length, visualStart, visualEnd} = getBasicFragmentSelectionDetails(
    endpoints,
    orientation,
    basicPolymerInfo,
    canSpecifyEndpoints
  );
  const selectionEndpointInputProps = getSelectionDisplayProps(
    endpoints,
    setEndpoints,
    orientation,
    setOrientation,
    basicPolymerInfo.startIndex,
    basicPolymerInfo.circular,
    restrictionEnzymeInfo5,
    restrictionEnzymeInfo3,
    basicPolymerInfo.singleStranded,
    basicPolymerInfo.residues.length
  );

  const snappableEndpoints = getSnappableCutsiteEndpoints(
    restrictionEnzymeInfo5.cutsiteIndices,
    restrictionEnzymeInfo3.cutsiteIndices,
    endpoints,
    basicPolymerInfo.residues.length,
    basicPolymerInfo.circular
  );

  return (
    <>
      <CardHeader polymerId={polymerId} binName={binName} />
      <div className={css(styles.inputsRow)}>
        {canSpecifyEndpoints && (
          <>
            <div className={css(styles.equalFlex)}>
              <SelectionEndpointInput {...selectionEndpointInputProps.start} isDisabled={isReadOnly} />
            </div>
            {!basicPolymerInfo.singleStranded ? (
              <Button
                buttonStyle={ButtonStyle.TERTIARY_GRAY}
                className={css(styles.switchIcon)}
                size={ButtonSize.SMALL}
                icon="exchange"
                onClick={() => {
                  analytics.trackEvent(
                    MOLBIO_ANALYTICS_ACTIONS.CLICKED_BULK_ASSEMBLY_FRAGMENT_SWAP_ENDPOINTS_BUTTON,
                    {
                      actionOrigin: ActionOrigin.MOLBIO_BULK_ASSEMBLY_FRAGMENT_MODAL,
                      assemblyId,
                    }
                  );
                  setOrientation(OPPOSITE_BA_ORIENTATION[orientation]);
                  if (basicPolymerInfo.circular) {
                    setEndpoints({start: endpoints.end, end: endpoints.start});
                  }
                }}
                disabled={isReadOnly}
              />
            ) : (
              <div className={css(styles.dashContainer)}>
                {/* Create empty label to occupy vertical space */}
                <div>
                  <FieldLabel label="" />
                </div>
                <BenchlingIcon className={css(styles.dash)} icon="minus" />
              </div>
            )}
            <div className={css(styles.equalFlex)}>
              <SelectionEndpointInput {...selectionEndpointInputProps.end} isDisabled={isReadOnly} />
            </div>
          </>
        )}
        {!basicPolymerInfo.singleStranded && (
          <div className={css(styles.equalFlex, styles.orientationMaxWidth)}>
            <FieldLabel label="Orientation" />
            <SelectDropdown
              // When polymer is linear then orientation is inferred by the endpoints, EXCEPT for homology
              // since users cannot specify endpoints for that method, but may still want the revcomp fragment.
              disabled={(!basicPolymerInfo.circular && canSpecifyEndpoints) || isReadOnly}
              displayName={convertStrandToDisplayName(BA_ORIENTATION_TO_STRAND[orientation])}
              menu={
                <Menu width={MenuWidth.FULL}>
                  <Option onSelect={setOrientation} value={BulkAssemblyOrientation.FORWARD}>
                    Forward
                  </Option>
                  <Option onSelect={setOrientation} value={BulkAssemblyOrientation.REVERSE}>
                    Reverse
                  </Option>
                </Menu>
              }
            />
          </div>
        )}
      </div>
      {validationFailureMessage && (
        <p>
          <span className={css(styles.validationError)}>{validationFailureMessage} </span>
          {snappableEndpoints != null && (
            <span>
              <Button
                buttonStyle={ButtonStyle.LINK}
                onClick={() => {
                  setEndpoints(snappableEndpoints);
                }}
              >
                Snap to nearest cut site(s)
              </Button>
            </span>
          )}
        </p>
      )}
      {showPrimerSearch && (
        <div className={css(styles.inputsRow)}>
          <div className={css(styles.equalFlex)}>
            <FieldLabel label="Preferred 5' primer" />
            <PrimerSearchInput
              primerId={preferredPrimer5Id}
              setPrimerId={setPreferredPrimer5Id}
              isReadOnly={isReadOnly}
            />
          </div>
          <div className={css(styles.equalFlex)}>
            <FieldLabel label="Preferred 3' primer" />
            <PrimerSearchInput
              primerId={preferredPrimer3Id}
              setPrimerId={setPreferredPrimer3Id}
              isReadOnly={isReadOnly}
            />
          </div>
        </div>
      )}
      {canUpdateRestrictionEnzymes && (
        <div className={css(styles.inputsRow)}>
          {restrictionEnzymeInfo5.restrictionEnzymeId != null && (
            <EndpointRestrictionEnzymeInput
              className={css(styles.equalFlex)}
              restrictionEnzymeId={restrictionEnzymeInfo5.restrictionEnzymeId}
              sequencePrime={SequencePrime.FIVE}
              updateRestrictionEnzymeId={setRestrictionEnzymeId5}
            />
          )}
          {restrictionEnzymeInfo3.restrictionEnzymeId != null && (
            <EndpointRestrictionEnzymeInput
              className={css(styles.equalFlex)}
              restrictionEnzymeId={restrictionEnzymeInfo3.restrictionEnzymeId}
              sequencePrime={SequencePrime.THREE}
              updateRestrictionEnzymeId={setRestrictionEnzymeId3}
            />
          )}
        </div>
      )}
      {hadStaleVersion && (
        <InlineBanner
          bannerType={InlineBannerType.WARNING}
          text={`Sequence has changed since it was added here.
 Please review your selections to make sure they're still valid.`}
        />
      )}
      <div className={css(styles.visualizationContainer)}>
        <FragmentVisualization
          sequence={{
            start: visualStart,
            end: visualEnd,
            sequenceLength: basicPolymerInfo.residues.length,
            strand: BA_ORIENTATION_TO_STRAND[orientation],
            circular: basicPolymerInfo.circular,
            fivePrimeDisplayInfo: {
              enzymeId: getEnzymeDisplayInfo(
                restrictionEnzymeInfo5,
                getValueForOrientation(orientation, endpoints.start, endpoints.end)
              ),
              bases: null,
              primer: false,
            },
            threePrimeDisplayInfo: {
              enzymeId: getEnzymeDisplayInfo(
                restrictionEnzymeInfo3,
                getValueForOrientation(orientation, endpoints.end, endpoints.start)
              ),
              bases: null,
              primer: false,
            },
          }}
          fragmentColor={color}
          width={200}
        />
      </div>
      <div className={css(styles.sizeText)}>
        {formatPolymerResidueSubcount(length, basicPolymerInfo.residues.length, basicPolymerInfo.residueType)}
      </div>
    </>
  );
}

const styles = StyleSheet.create({
  clickable: {
    cursor: 'pointer',
  },
  inputsRow: {
    display: 'flex',
    gap: '6px',
    padding: '8px 0px',
  },
  equalFlex: {
    flex: 1,
  },
  visualizationContainer: {
    display: 'flex',
    justifyContent: 'center',
  },
  validationError: {
    color: RED_DARK,
  },
  switchIcon: {
    alignSelf: 'flex-end',
    boxShadow: 'none',
  },
  sizeText: {
    textAlign: 'center',
  },
  binName: {
    color: GRAY_DARKER,
    marginBottom: '8px',
  },
  dash: {
    flex: 1,
    width: '100%',
  },
  dashContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  orientationMaxWidth: {
    maxWidth: '96px',
  },
});
